import type { DeriveInviteViewPhaseInput, InviteViewPhase } from "./inviteTypes";
import {
  hasPlatformErrors,
  isOAuthReturn,
  isPlatformDisconnected,
  isPlatformSuccess,
} from "./inviteTypes";
import { mapPodscopePhaseToPrixi } from "./invitePhaseUtils";

function deriveBaseInviteViewPhase(input: DeriveInviteViewPhaseInput): InviteViewPhase {
  const {
    loading,
    revalidating,
    error,
    unavailableReason,
    inviteExpired,
    socialAccountId,
    hasInviteData,
    urlParams,
    metaNeedsPageId,
    metaConnectStep,
    facebookAccessToken,
    inFlight,
    platformErrors,
  } = input;

  if (error && !hasInviteData && !unavailableReason) {
    return "fatal";
  }

  const oauthReturn = isOAuthReturn(urlParams);

  if ((loading || revalidating) && !hasInviteData && !unavailableReason) {
    return oauthReturn ? "revalidating" : "loading";
  }

  if (revalidating && hasInviteData) {
    return "revalidating";
  }

  if (loading && hasInviteData) {
    return "revalidating";
  }

  if (isPlatformDisconnected(urlParams)) {
    return "disconnected";
  }

  if (isPlatformSuccess(urlParams)) {
    return "success";
  }

  if (unavailableReason === "expired" || inviteExpired) {
    return "expired";
  }

  if (
    unavailableReason === "revoked" ||
    unavailableReason === "already_used"
  ) {
    return "unavailable";
  }

  if (error && hasInviteData) {
    return "fatal";
  }

  if (socialAccountId) {
    return "already_connected";
  }

  const isMetaConnecting =
    inFlight.facebookLoading ||
    inFlight.metaSubmitting ||
    (!!facebookAccessToken && !metaNeedsPageId && !metaConnectStep);

  if (inFlight.facebookLoading || inFlight.metaSubmitting) {
    return "connecting";
  }

  if (metaConnectStep === "mapping") {
    return "meta_mapping";
  }

  if (metaConnectStep === "select") {
    return "meta_podcast_select";
  }

  if (isMetaConnecting || inFlight.spotifyLoading) {
    return "connecting";
  }

  if (urlParams.errorParam || hasPlatformErrors(platformErrors)) {
    return "connect_error";
  }

  return "connect";
}

export function deriveInviteViewPhase(input: DeriveInviteViewPhaseInput): InviteViewPhase {
  const basePhase = deriveBaseInviteViewPhase(input);

  if (input.integrationSource === "prixi") {
    return mapPodscopePhaseToPrixi(basePhase);
  }

  return basePhase;
}
